import React, { useState } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import PageTransition from '../components/layout/PageTransition';
import Reveal from '../components/ui/Reveal';
import Icon from '../components/ui/Icon';
import { COMPANY } from '../data/site';

const SERVICE_ID  = 'service_cts26ds';
const TEMPLATE_ID = 'template_alfrkmf';
const USER_ID     = 'IAjNvr2p1cJMrIO4C';

const BUDGETS = [
  'Not sure yet',
  'Under $25k',
  '$25k – $80k',
  '$80k – $200k',
  '$200k+',
];
const SERVICES_OPTIONS = [
  'AI / automation',
  'Web app',
  'Mobile app',
  'Custom software',
  'Strategy / consulting',
  'Other',
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: '', email: '', company: '', service: '', budget: '', message: '',
  });
  const [status, setStatus] = useState({ kind: 'idle', msg: '' }); // idle | sending | success | error
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (status.kind === 'sending') return;
    setStatus({ kind: 'sending', msg: 'Sending…' });

    // The existing EmailJS template only renders {{name}}, {{email}}, {{message}}.
    // Fold the structured fields (company/service/budget) into the message body
    // so they reach the inbox without requiring a template edit on emailjs.com.
    const header = [
      form.company && `Company: ${form.company}`,
      form.service && `Service: ${form.service}`,
      form.budget  && `Budget: ${form.budget}`,
    ].filter(Boolean).join('\n');
    const payload = {
      name:    form.name,
      email:   form.email,
      message: header ? `${header}\n\n---\n\n${form.message}` : form.message,
    };

    emailjs.send(SERVICE_ID, TEMPLATE_ID, payload, USER_ID)
      .then(() => {
        setStatus({ kind: 'success', msg: 'Message sent. We\'ll be in touch within one business day.' });
        setForm({ name: '', email: '', company: '', service: '', budget: '', message: '' });
      })
      .catch(() => setStatus({ kind: 'error', msg: 'Something went wrong. Email us directly at ' + COMPANY.email }));
  };

  return (
    <PageTransition>
      <section className="page-hero">
        <div className="container">
          <Reveal><span className="eyebrow"><span className="dot" />Start a project</span></Reveal>
          <Reveal delay={0.06} as="h1">
            Tell us what<br />you're building.
          </Reveal>
          <Reveal delay={0.12}>
            <p className="lede">
              15-minute intro call, no pitch deck, no commitment. By the end you'll have a clearer
              plan — whether you work with us or not. We respond within one business day.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="section--tight">
        <div className="container">
          <div className="contact-layout">
            <div className="contact-info">
              <div className="contact-info-block">
                <div className="label">Email</div>
                <div className="val"><a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></div>
                <div className="small">Best for project inquiries — read by the founders.</div>
              </div>

              <div className="contact-info-block">
                <div className="label">Phone</div>
                {COMPANY.phones.map((p) => (
                  <div key={p} className="val" style={{ fontSize: 18 }}>
                    <a href={`tel:${p.replace(/\s/g, '')}`}>{p}</a>
                  </div>
                ))}
                <div className="small" style={{ marginTop: 8 }}>Mon–Fri · 10:00–19:00 IST</div>
              </div>

              {COMPANY.social.linkedin && (
                <div className="contact-info-block">
                  <div className="label">LinkedIn</div>
                  <div className="val">
                    <a href={COMPANY.social.linkedin} target="_blank" rel="noopener noreferrer" className="contact-linkedin">
                      <Icon name="linkedin" size={18} />
                      <span>@the-voila-tech</span>
                      <Icon name="external" size={14} style={{ opacity: 0.55 }} />
                    </a>
                  </div>
                  <div className="small">DM us or follow for case studies and field notes.</div>
                </div>
              )}

              <div className="contact-info-block">
                <div className="label">Studios</div>
                <div className="val">{COMPANY.locations.join(' · ')}</div>
                <div className="small">India · Working with clients across PT, ET, and CET</div>
              </div>

              <div className="contact-info-block">
                <div className="label">Currently booking</div>
                <div className="val">{COMPANY.status.replace('Now booking · ', '')} · 2 slots</div>
                <div className="small">Engagements typically start 4–6 weeks after first contact.</div>
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                style={{ borderTop: '1px solid var(--border)', paddingTop: 24, marginTop: 16 }}
              >
                <div className="label" style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.12em', color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 12 }}>What happens next</div>
                <ol className="next-steps">
                  {[
                    'We read your message and match it to the right team',
                    'You get a reply within 1 business day, usually with 2–3 questions',
                    'We schedule a 15-minute intro call',
                    'You get a written plan — buildable, regardless of who builds it',
                  ].map((s, i) => (
                    <li key={s}>
                      <span className="num">{String(i + 1).padStart(2, '0')}</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ol>
              </motion.div>
            </div>

            <motion.form
              className="contact-form"
              onSubmit={submit}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 0.84, 0.44, 1] }}
            >
              <div className="field-row">
                <div className="field">
                  <label htmlFor="name">Your name</label>
                  <input id="name" required value={form.name} onChange={set('name')} placeholder="Jane Doe" />
                </div>
                <div className="field">
                  <label htmlFor="email">Email</label>
                  <input id="email" type="email" required value={form.email} onChange={set('email')} placeholder="jane@company.com" />
                </div>
              </div>

              <div className="field">
                <label htmlFor="company">Company / project</label>
                <input id="company" value={form.company} onChange={set('company')} placeholder="Acme Inc." />
              </div>

              <div className="field-row">
                <div className="field">
                  <label htmlFor="service">What do you need?</label>
                  <select id="service" value={form.service} onChange={set('service')}>
                    <option value="">Select…</option>
                    {SERVICES_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="budget">Budget range</label>
                  <select id="budget" value={form.budget} onChange={set('budget')}>
                    <option value="">Select…</option>
                    {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              </div>

              <div className="field">
                <label htmlFor="message">Tell us about the project</label>
                <textarea
                  id="message"
                  required
                  rows={6}
                  value={form.message}
                  onChange={set('message')}
                  placeholder="What are you trying to build, who is it for, and what does success look like?"
                />
              </div>

              {status.kind !== 'idle' && status.kind !== 'sending' && (
                <div className={'form-status ' + status.kind}>{status.msg}</div>
              )}

              <button
                type="submit"
                className="btn btn--primary btn--lg"
                disabled={status.kind === 'sending'}
                style={{ justifyContent: 'center', marginTop: 8, opacity: status.kind === 'sending' ? 0.7 : 1 }}
              >
                {status.kind === 'sending' ? 'Sending…' : 'Send message'}
                <Icon name="arrow" size={14} className="arrow" />
              </button>

              <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted-2)', letterSpacing: '0.04em', textAlign: 'center', marginTop: 4 }}>
                We read every message. No autoresponders, no funnels.
              </div>
            </motion.form>
          </div>
        </div>
      </section>
    </PageTransition>
  );
}
